import { create } from "zustand";

export type AddAlbumWizardStep = "album-import" | "select-songs" | "choose-writers" | "assign-writers" | "details-and-shares" | "summary-and-submit";

type AddAlbumWizardState = {
  currentStep: AddAlbumWizardStep;
  completedSteps: Set<AddAlbumWizardStep>;
  canNavigateToStep: (step: AddAlbumWizardStep) => boolean;
  goToStep: (step: AddAlbumWizardStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  markStepComplete: (step: AddAlbumWizardStep) => void;
  resetWizard: () => void;
};

const stepOrder: AddAlbumWizardStep[] = ["album-import", "select-songs", "choose-writers", "assign-writers", "details-and-shares", "summary-and-submit"];

export const useAddAlbumWizardStore = create<AddAlbumWizardState>((set, get) => ({
  currentStep: "album-import",
  completedSteps: new Set(),

  canNavigateToStep: (step: AddAlbumWizardStep) => {
    const { completedSteps } = get();
    const stepIndex = stepOrder.indexOf(step);
    const currentIndex = stepOrder.indexOf(get().currentStep);

    // Can always go backwards to completed steps
    if (stepIndex < currentIndex && completedSteps.has(step)) {
      return true;
    }

    // Can go forward only if all previous steps are completed
    for (let i = 0; i < stepIndex; i++) {
      if (!completedSteps.has(stepOrder[i])) {
        return false;
      }
    }

    return true;
  },

  goToStep: (step: AddAlbumWizardStep) => {
    const { canNavigateToStep } = get();
    if (canNavigateToStep(step)) {
      set({ currentStep: step });
    }
  },

  nextStep: () => {
    const { currentStep } = get();
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      set({ currentStep: stepOrder[currentIndex + 1] });
    }
  },

  previousStep: () => {
    const { currentStep } = get();
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      set({ currentStep: stepOrder[currentIndex - 1] });
    }
  },

  markStepComplete: (step: AddAlbumWizardStep) => {
    set(state => ({
      completedSteps: new Set([...state.completedSteps, step]),
    }));
  },

  resetWizard: () => {
    set({
      currentStep: "album-import",
      completedSteps: new Set(),
    });

    // Also reset other stores
    // Import and clear the assignment store
    import("./songWriterAssignmentStore").then((module) => {
      module.default.getState().clearState();
    }).catch(console.error);
  },
}));
